import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Stack,
  Chip,
  List,
  ListItem,
  Avatar,
  Tooltip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Alert
} from "@mui/material";
import {
  AttachMoney,
  Info,
  CheckCircle,
  Cancel,
  ArrowDownward,
  CancelOutlined,
  WarningAmber,
  Restore,
  InfoOutlined
} from "@mui/icons-material";
import CardLists from "../Extra/CardsList";
import { useDispatch, useSelector } from "react-redux";
import {
  selectCurrentSuperAdmin,
  selectSubAdminBillingDetailsdData
} from "../../features/auth/authSelectors";
import {
  getCurrentUser,
  getSubAdminDashboardDetails
} from "../../features/auth/authSlice";
import { downgradeSubscription, cancelSubscription } from "../../api/authApi";
import dayjs from "dayjs";
import { currencySymbols } from "../../utils/countryRatesMapping";
const gradientBg =
  "linear-gradient(135deg, rgba(54, 162, 235, 0.03), rgba(75, 192, 192, 0.01))";
const userGradientBg = "linear-gradient(135deg, #ff980008, #ff980002)";
const borderColor = "1px solid rgba(54, 162, 235, 0.08)";
const dangerGradientBg = "linear-gradient(135deg, #ffebee, #ffcdd2)";
const dangerBorderColor = "1px solid rgba(244, 67, 54, 0.08)";

const paperProps = {
  elevation: 1,
  sx: {
    p: 3,
    borderRadius: 4,
    background: gradientBg,
    border: borderColor,
    transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 12px 32px rgba(0,0,0,0.08)"
    }
  }
};

const userPaperProps = {
  elevation: 1,
  sx: {
    p: 3,
    borderRadius: 4,
    background: userGradientBg,
    border: borderColor,
    transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 12px 32px rgba(0,0,0,0.08)"
    }
  }
};

const dangerPaperProps = {
  elevation: 1,
  sx: {
    p: 3,
    borderRadius: 4,
    background: dangerGradientBg,
    border: dangerBorderColor,
    transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 12px 32px rgba(0,0,0,0.08)"
    }
  }
};

const AdminDashboard = () => {
  const currentAdmin = useSelector(selectCurrentSuperAdmin);
  const dispatch = useDispatch();
  const dashboardData = useSelector(selectSubAdminBillingDetailsdData);
  useEffect(() => {
    dispatch(getSubAdminDashboardDetails());
  }, [dispatch]);
  const isNotAffiliateAdmin = currentAdmin?.userType !== "Affiliate";
  const [openModal, setOpenModal] = useState(false);
  const [openCancelModal, setOpenCancelModal] = useState(false);

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  const handleOpenCancelModal = () => setOpenCancelModal(true);
  const handleCloseCancelModal = () => setOpenCancelModal(false);

  const handleDowngrade = async () => {
    await downgradeSubscription({
      amount: dashboardData?.downgradeOptions?.amount,
      limit: dashboardData?.downgradeOptions?.limit,
      userType: currentAdmin?.userType?.toLowerCase(),
      stripeCustomerId: currentAdmin?.stripeCustomerId,
      referenceId: currentAdmin?.referenceId
    });
    await dispatch(getSubAdminDashboardDetails());
    await dispatch(getCurrentUser());
    handleCloseModal();
  };

  const handleCancelSubscription = async () => {
    await cancelSubscription({
      userType: currentAdmin?.userType?.toLowerCase(),
      stripeCustomerId: currentAdmin?.stripeCustomerId,
      referenceId: currentAdmin?.referenceId
    });
    await dispatch(getSubAdminDashboardDetails());
    await dispatch(getCurrentUser());
    handleCloseCancelModal();
  };

  return (
    <Box>
      {!isNotAffiliateAdmin ? (
        <></>
      ) : (
        <>
          {" "}
          <CardLists />
          <Box
            sx={{
              display: "flex",
              gap: "10px",
              my: 3,
              p: 3,
              backgroundColor: "rgb(248 246 249)",
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              alignItems: "center",
              position: "relative",
            }}
          >
            <Avatar
              sx={{
                bgcolor: "#e91e63",
                mr: 2,
                width: 40,
                height: 40,
                fontSize: 20,
                color: "#ffffff",
              }}
            >
              T
            </Avatar>
            <Stack
              spacing={2}
              display={"flex"}
              flexDirection={"row"}
              width={"100%"}
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Stack spacing={0.5}>
                <Typography
                  fontWeight="bold"
                  sx={{
                    color: "#e91e63",
                    fontSize: "1.1rem",
                  }}
                >
                  Current Tier
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#7f8c8d",
                    fontSize: "0.9rem",
                  }}
                >
                  0 - {currentAdmin?.referenceData?.maxSeats} Users
                </Typography>
              </Stack>
              <Box display="flex" gap="10px">
                {dashboardData?.downgradeOptions &&
                  currentAdmin?.userType !== "Gym" && (
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<ArrowDownward />}
                      onClick={handleOpenModal}
                      sx={{
                        mt: 2,
                        textTransform: "none",
                        borderRadius: 2,
                        "&:hover": {
                          backgroundColor: "#fff3e0",
                        },
                      }}
                    >
                      Downgrade Plan
                    </Button>
                  )}{" "}
                <Tooltip
                  title={`Your current subscription allows up to ${currentAdmin?.referenceData?.maxSeats} users.`}
                >
                  <IconButton
                    size="small"
                    sx={{ position: "absolute", top: 10, right: 10 }}
                  >
                    <Info />
                  </IconButton>
                </Tooltip>
              </Box>
            </Stack>
          </Box>
          {/* Cancel Subscription Box */}
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper {...dangerPaperProps}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor:
                          currentAdmin?.activeSubscription?.status !== "active"
                            ? "#9e9e9e"
                            : "#f44336",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CancelOutlined sx={{ fontSize: 24, color: "white" }} />
                    </Box>
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      color={
                        currentAdmin?.activeSubscription?.status !== "active"
                          ? "#9e9e9e"
                          : "#f44336"
                      }
                    >
                      {currentAdmin?.activeSubscription?.status !== "active"
                        ? "Subscription Cancelled"
                        : "Cancel Subscription"}
                    </Typography>
                  </Stack>
                  {currentAdmin?.activeSubscription?.status !== "active" && (
                    <Chip
                      label="Cancelled"
                      color="default"
                      size="small"
                      sx={{ fontWeight: 500 }}
                    />
                  )}
                </Stack>

                {currentAdmin?.activeSubscription?.status !== "active" ? (
                  <Box
                    sx={{ bgcolor: "rgba(0,0,0,0.03)", p: 2, borderRadius: 2 }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <InfoOutlined color="action" fontSize="small" />
                      <Typography variant="body2" color="text.secondary">
                        Access remains active through{" "}
                        <Typography
                          component="span"
                          fontWeight="bold"
                          color="text.primary"
                        >
                          {dayjs(
                            currentAdmin?.activeSubscription?.currentPeriodEnd,
                          ).format("MMMM DD, YYYY")}
                        </Typography>
                        {". "}No further renewals will occur.
                      </Typography>
                    </Stack>
                  </Box>
                ) : (
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ my: 1 }}>
                      <Typography variant="body1" mb={1} fontWeight={500}>
                        If you cancel your subscription:
                      </Typography>
                      {currentAdmin?.userType === "Coach" ? (
                        <List sx={{ listStyleType: "disc", pl: 4 }}>
                          <ListItem sx={{ display: "list-item", py: 0.5 }}>
                            <Typography variant="body2">
                              Your subscription will end immediately{" "}
                              {/* <Typography component="span" fontWeight="bold">
                              {dayjs(
                                currentAdmin?.activeSubscription
                                  ?.currentPeriodEnd || new Date()
                              ).format("MMMM DD, YYYY")}
                            </Typography> */}
                            </Typography>
                          </ListItem>
                          {/* <ListItem sx={{ display: "list-item", py: 0.5 }}>
                          <Typography variant="body2">
                            You'll continue to have access to all features
                            during this period
                          </Typography>
                        </ListItem> */}
                          <ListItem sx={{ display: "list-item", py: 0.5 }}>
                            {/* <Typography variant="body2" color="error.main">
                            All your users and associated data will be
                            permanently removed after the billing period ends.
                          </Typography> */}
                            <Typography variant="body2" color="error.main">
                              Users will be prompted to pay for a subscription
                              to maintain access to any purchased programs and
                              for continued diary logging.
                            </Typography>
                          </ListItem>
                        </List>
                      ) : currentAdmin?.userType === "Gym" ?
                      (
                        <List sx={{ listStyleType: "disc", pl: 4 }}>
                          <ListItem sx={{ display: "list-item", py: 0.5 }}>
                            <Typography variant="body2">
                              Your subscription will remain active until{" "}
                              <Typography component="span" fontWeight="bold">
                                {dayjs(
                                  currentAdmin?.activeSubscription
                                    ?.currentPeriodEnd || new Date(),
                                ).format("MMMM DD, YYYY")}
                              </Typography>
                            </Typography>
                          </ListItem>
                          <ListItem sx={{ display: "list-item", py: 0.5 }}>
                            <Typography variant="body2">
                              You'll continue to have access to all features
                              during this period
                            </Typography>
                          </ListItem>
                          <ListItem sx={{ display: "list-item", py: 0.5 }}>
                            <Typography variant="body2" color="error.main">
                              Your users will be removed from your account at that time. 
                            </Typography>
                            <Typography variant="body2" color="error.main">
                              Their subscriptions will remain active as a general app user until they cancel them.
                              </Typography>
                          </ListItem>
                        </List>
                      ) : currentAdmin?.userType === "Company" ?
                        (
                        <List sx={{ listStyleType: "disc", pl: 4 }}>
                          <ListItem sx={{ display: "list-item", py: 0.5 }}>
                            <Typography variant="body2">
                              Your subscription will remain active until{" "}
                              <Typography component="span" fontWeight="bold">
                                {dayjs(
                                  currentAdmin?.activeSubscription
                                    ?.currentPeriodEnd || new Date(),
                                ).format("MMMM DD, YYYY")}
                              </Typography>
                            </Typography>
                          </ListItem>
                          <ListItem sx={{ display: "list-item", py: 0.5 }}>
                            <Typography variant="body2">
                              You'll continue to have access to all features
                              during this period
                            </Typography>
                          </ListItem>
                          <ListItem sx={{ display: "list-item", py: 0.5 }}>
                            <Typography variant="body2" color="error.main">
                              All your users and associated data will be
                              permanently removed after the billing period ends.
                            </Typography>
                            <Typography variant="body2" color="error.main">
                              Users will be prompted to pay for a subscription
                              to maintain access.
                            </Typography>
                          </ListItem>
                        </List>
                      ) :
                      (
                        <List sx={{ listStyleType: "disc", pl: 4 }}>
                          <ListItem sx={{ display: "list-item", py: 0.5 }}>
                            <Typography variant="body2">
                              Your subscription will remain active until{" "}
                              <Typography component="span" fontWeight="bold">
                                {dayjs(
                                  currentAdmin?.activeSubscription
                                    ?.currentPeriodEnd || new Date(),
                                ).format("MMMM DD, YYYY")}
                              </Typography>
                            </Typography>
                          </ListItem>
                          <ListItem sx={{ display: "list-item", py: 0.5 }}>
                            <Typography variant="body2">
                              You'll continue to have access to all features
                              during this period
                            </Typography>
                          </ListItem>
                          <ListItem sx={{ display: "list-item", py: 0.5 }}>
                            <Typography variant="body2" color="error.main">
                              All your users and associated data will be
                              permanently removed after the billing period ends.
                            </Typography>
                            <Typography variant="body2" color="error.main">
                              Users will be prompted to pay for a subscription
                              to maintain access to any purchased
                              programs and for continued diary logging.
                            </Typography>
                          </ListItem>
                        </List>
                      )}
                    </Box>
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<Cancel />}
                      onClick={handleOpenCancelModal}
                      sx={{
                        textTransform: "none",
                        height: "100%",
                        borderRadius: 2,
                      }}
                    >
                      Cancel Subscription
                    </Button>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
          <Grid container spacing={3} mt={2}>
            <Grid item xs={12}>
              <Paper {...paperProps}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: "#36a2eb",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <AttachMoney sx={{ fontSize: 24, color: "white" }} />
                    </Box>
                    <Typography variant="h6" fontWeight="bold" color="#36a2eb">
                      Billing History
                    </Typography>
                  </Stack>
                </Stack>
                <List sx={{ width: "100%", p: 0 }}>
                  {dashboardData?.subAdminInvoices?.map(
                    (transaction, index) => (
                      <ListItem
                        key={index}
                        sx={{
                          py: 2,
                          px: 3,
                          my: 1,
                          borderRadius: 2,
                          bgcolor: "white",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                            background: gradientBg,
                            transform: "translateY(-2px)",
                          },
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          width="100%"
                        >
                          <Typography
                            variant="body1"
                            fontWeight={500}
                            sx={{ flex: 1, color: "#2c3e50" }}
                          >
                            {transaction?.product?.[0]?.description}
                          </Typography>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                          >
                            <Typography
                              variant="body2"
                              sx={{ color: "#7f8c8d", minWidth: "80px" }}
                            >
                              {transaction?.product?.[0]?.purchaseDate}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color:
                                  transaction.status === "paid"
                                    ? "#27ae60"
                                    : transaction.status === "failed"
                                      ? "#e74c3c"
                                      : "#ff9800",
                                fontWeight: 500,
                                minWidth: "80px",
                              }}
                            >
                              ${transaction.amount.toFixed(2)}
                            </Typography>
                            <Chip
                              label={
                                transaction.status === "paid"
                                  ? "Success"
                                  : transaction.status === "failed"
                                    ? "Failed"
                                    : "Open"
                              }
                              icon={
                                transaction.status === "paid" ? (
                                  <CheckCircle sx={{ fontSize: 16 }} />
                                ) : transaction.status === "failed" ? (
                                  <Cancel sx={{ fontSize: 16 }} />
                                ) : (
                                  <Info sx={{ fontSize: 16 }} />
                                )
                              }
                              sx={
                                transaction.status === "paid"
                                  ? {
                                      bgcolor: "#eafaf1",
                                      color: "#27ae60",
                                      fontWeight: 500,
                                    }
                                  : transaction.status === "failed"
                                    ? {
                                        bgcolor: "#ffeaea",
                                        color: "#e74c3c",
                                        fontWeight: 500,
                                      }
                                    : {
                                        bgcolor: "#fff4e5",
                                        color: "#ff9800",
                                        fontWeight: 500,
                                      }
                              }
                            />
                          </Stack>
                        </Stack>
                      </ListItem>
                    ),
                  )}
                </List>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
      {currentAdmin?.userType !== "Coach" && (
        <Grid container mt={2} spacing={3}>
          <Grid pl={"30px"} item xs={12}>
            <Paper {...userPaperProps}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: "#ff9800",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <AttachMoney sx={{ fontSize: 24, color: "white" }} />
                  </Box>
                  <Typography display={"flex"} gap="10px" alignItems="center">
                    <Typography variant="h6" fontWeight="bold" color="#ff9800">
                      User Transactions{" "}
                    </Typography>
                    <Typography color="red">
                      {!["Company"].includes(currentAdmin?.userType)
                        ? "($ values shown are still subject to % split)"
                        : ""}
                    </Typography>
                  </Typography>
                </Stack>
              </Stack>
              <List sx={{ width: "100%", p: 0 }}>
                {dashboardData?.userTransactions?.map((transaction, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      py: 2,
                      px: 3,
                      my: 1,
                      borderRadius: 2,
                      bgcolor: "white",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        background: userGradientBg,
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      width="100%"
                    >
                      <Typography
                        variant="body1"
                        fontWeight={500}
                        sx={{ flex: 1, color: "#2c3e50" }}
                      >
                        {transaction?.product?.[0]?.description}
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Typography
                          variant="body2"
                          sx={{ color: "#7f8c8d", minWidth: "80px" }}
                        >
                          {transaction?.product?.[0]?.purchaseDate}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color:
                              transaction.status === "paid"
                                ? "#27ae60"
                                : transaction.status === "failed"
                                  ? "#e74c3c"
                                  : "#ff9800",
                            fontWeight: 500,
                            minWidth: "80px",
                          }}
                        >
                          {currencySymbols[transaction?.currency]}
                          {transaction.amount.toFixed(2)}
                        </Typography>
                        <Chip
                          label={
                            transaction.status === "paid"
                              ? "Success"
                              : transaction.status === "failed"
                                ? "Failed"
                                : "Open"
                          }
                          icon={
                            transaction.status === "paid" ? (
                              <CheckCircle sx={{ fontSize: 16 }} />
                            ) : transaction.status === "failed" ? (
                              <Cancel sx={{ fontSize: 16 }} />
                            ) : (
                              <Info sx={{ fontSize: 16 }} />
                            )
                          }
                          sx={
                            transaction.status === "paid"
                              ? {
                                  bgcolor: "#eafaf1",
                                  color: "#27ae60",
                                  fontWeight: 500,
                                }
                              : transaction.status === "failed"
                                ? {
                                    bgcolor: "#ffeaea",
                                    color: "#e74c3c",
                                    fontWeight: 500,
                                  }
                                : {
                                    bgcolor: "#fff4e5",
                                    color: "#ff9800",
                                    fontWeight: 500,
                                  }
                          }
                        />
                      </Stack>
                    </Stack>
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Downgrade Modal */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: "#fff3e0", color: "#ed6c02" }}>
          Confirm Plan Downgrade
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Stack spacing={2}>
            <Typography variant="body1">
              Are you sure you want to downgrade your plan?
            </Typography>
            <Box sx={{ bgcolor: "#f5f5f5", p: 2, borderRadius: 1 }}>
              <Stack spacing={1}>
                <Typography variant="subtitle2">
                  Next Charge: ${dashboardData?.downgradeOptions?.amount}
                </Typography>
                <Typography variant="subtitle2">
                  New User Limit: {dashboardData?.downgradeOptions?.limit} users
                </Typography>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseModal} variant="outlined" color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleDowngrade}
            variant="contained"
            color="warning"
            sx={{ ml: 1 }}
          >
            Confirm Downgrade
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Subscription Modal */}
      <Dialog
        open={openCancelModal}
        onClose={handleCloseCancelModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            bgcolor: "#ffebee",
            color: "#d32f2f",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <WarningAmber />
          Cancel Subscription
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            This action cannot be reversed once confirmed.
          </Alert>
          <Typography variant="body1" fontWeight={500} gutterBottom>
            Are you sure you want to cancel your subscription?
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            By confirming, you acknowledge that:
          </Typography>
          <List sx={{ listStyleType: "disc", pl: 2 }}>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>
              <Typography variant="body2">
                {currentAdmin?.userType === "Coach"
                  ? "Your subscription will end immediately "
                  : `Your subscription will remain active until the end of the
                current billing period: `}
                <strong>
                  {dayjs(
                    currentAdmin?.activeSubscription?.currentPeriodEnd,
                  ).format("DD.MM.YYYY") || "Next billing date"}
                </strong>
              </Typography>
            </ListItem>
            {console.log("currentAdmin?.userType", currentAdmin?.userType)}
            <ListItem sx={{ display: "list-item", py: 0.5 }}>
              <Typography variant="body2" color="error.main">
                {currentAdmin?.userType === "Coach"
                  ? `All your users and associated data will be permanently removed. Users will be prompted to pay for
                a subscription to maintain access to any purchased
                programs and for continued diary logging. `
                  : 
                  currentAdmin?.userType ===  "Company" ? 
                  `All your users and associated data will be permanently removed
                after the billing period ends. Users will be prompted to pay for
                a subscription to maintain access.`
                  :
                  currentAdmin?.userType ===  "Gym" ? 
                  `Your users will be removed from your account at that time. Their subscriptions will remain active as a general app user until they cancel them.`
                  :
                  `All your users and associated data will be permanently removed
                after the billing period ends. Users will be prompted to pay for
                a subscription to maintain access to any purchased
                programs and for continued diary logging. `}
              </Typography>
            </ListItem>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>
              <Typography variant="body2">
                To regain access, you will need to create a new account
              </Typography>
            </ListItem>
          </List>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" color="text.secondary">
            If you're experiencing issues with our service, please contact
            support before cancelling.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseCancelModal}
            variant="outlined"
            color="inherit"
          >
            Keep My Subscription
          </Button>
          <Button
            onClick={handleCancelSubscription}
            variant="contained"
            color="error"
            sx={{ ml: 1 }}
          >
            Cancel Subscription
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminDashboard;
